import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PhoneIcon,
  UserGroupIcon,
  CalendarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useBusiness } from '../context/BusinessContext';
import { listCalls, listLeads, listAppointments } from '../services/api';
import { CallLog, Lead, Appointment } from '../types';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek } from 'date-fns';

export default function DashboardPage() {
  const { currentBusiness, loading: businessLoading } = useBusiness();
  const [calls, setCalls] = useState<CallLog[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentBusiness) return;

      setLoading(true);
      try {
        const now = new Date();
        const weekStart = startOfWeek(now);
        const weekEnd = endOfWeek(now);

        const [callsData, leadsData, appointmentsData] = await Promise.all([
          listCalls(currentBusiness.id, 10),
          listLeads(currentBusiness.id, undefined, 10),
          listAppointments(
            currentBusiness.id,
            weekStart.toISOString(),
            weekEnd.toISOString()
          ),
        ]);

        setCalls(callsData);
        setLeads(leadsData);
        setAppointments(appointmentsData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentBusiness]);

  if (businessLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!currentBusiness) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">No Business Selected</h2>
        <p className="mt-2 text-gray-600">
          Please select a business or{' '}
          <Link to="/onboarding" className="text-primary-600 hover:underline">
            create a new one
          </Link>
          .
        </p>
      </div>
    );
  }

  const stats = [
    {
      name: 'Total Calls Today',
      value: calls.filter(
        (c) =>
          c.createdAt?.toDate() >= startOfDay(new Date()) &&
          c.createdAt?.toDate() <= endOfDay(new Date())
      ).length,
      icon: PhoneIcon,
      href: '/calls',
    },
    {
      name: 'New Leads',
      value: leads.filter((l) => l.status === 'new').length,
      icon: UserGroupIcon,
      href: '/leads',
    },
    {
      name: 'Upcoming Appointments',
      value: appointments.filter((a) => a.status === 'scheduled').length,
      icon: CalendarIcon,
      href: '/appointments',
    },
    {
      name: 'Avg. Call Duration',
      value: calls.length > 0
        ? `${Math.round(
            calls.reduce((sum, c) => sum + (c.duration || 0), 0) / calls.length / 60
          )}m`
        : '0m',
      icon: ClockIcon,
      href: '/calls',
    },
  ];

  return (
    <div>
      <h1 className="page-header">Dashboard</h1>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="card hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <stat.icon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Calls */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Recent Calls</h2>
            <Link to="/calls" className="text-sm text-primary-600 hover:underline">
              View all
            </Link>
          </div>
          {loading ? (
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 bg-gray-200 rounded"></div>
              ))}
            </div>
          ) : calls.length === 0 ? (
            <p className="text-gray-500 text-sm">No calls yet</p>
          ) : (
            <div className="space-y-4">
              {calls.slice(0, 5).map((call) => (
                <Link
                  key={call.id}
                  to={`/calls/${call.id}`}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50"
                >
                  <div>
                    <p className="font-medium text-gray-900">{call.callerNumber}</p>
                    <p className="text-sm text-gray-500">
                      {call.createdAt?.toDate()
                        ? format(call.createdAt.toDate(), 'MMM d, h:mm a')
                        : 'Unknown'}
                    </p>
                  </div>
                  <div className="text-right">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        call.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {call.status}
                    </span>
                    <p className="text-sm text-gray-500 mt-1">
                      {call.duration ? `${Math.round(call.duration / 60)}m` : '-'}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent Leads */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Recent Leads</h2>
            <Link to="/leads" className="text-sm text-primary-600 hover:underline">
              View all
            </Link>
          </div>
          {loading ? (
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 bg-gray-200 rounded"></div>
              ))}
            </div>
          ) : leads.length === 0 ? (
            <p className="text-gray-500 text-sm">No leads yet</p>
          ) : (
            <div className="space-y-4">
              {leads.slice(0, 5).map((lead) => (
                <div
                  key={lead.id}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {lead.callerName || lead.callerPhone}
                    </p>
                    <p className="text-sm text-gray-500">
                      {lead.reason || 'No reason specified'}
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      lead.status === 'new'
                        ? 'bg-blue-100 text-blue-800'
                        : lead.status === 'qualified'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {lead.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upcoming Appointments */}
      <div className="card mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">This Week's Appointments</h2>
          <Link to="/appointments" className="text-sm text-primary-600 hover:underline">
            View all
          </Link>
        </div>
        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        ) : appointments.length === 0 ? (
          <p className="text-gray-500 text-sm">No appointments scheduled</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Customer</th>
                  <th className="table-header">Service</th>
                  <th className="table-header">Date & Time</th>
                  <th className="table-header">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {appointments.slice(0, 5).map((appointment) => (
                  <tr key={appointment.id}>
                    <td className="table-cell">
                      <div>
                        <p className="font-medium">{appointment.customerName}</p>
                        <p className="text-gray-500 text-xs">{appointment.customerPhone}</p>
                      </div>
                    </td>
                    <td className="table-cell">{appointment.serviceName}</td>
                    <td className="table-cell">
                      {appointment.scheduledAt?.toDate()
                        ? format(appointment.scheduledAt.toDate(), 'MMM d, h:mm a')
                        : 'Unknown'}
                    </td>
                    <td className="table-cell">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          appointment.status === 'scheduled'
                            ? 'bg-blue-100 text-blue-800'
                            : appointment.status === 'confirmed'
                            ? 'bg-green-100 text-green-800'
                            : appointment.status === 'completed'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {appointment.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
